import { useCallback, useEffect, useState } from "react";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail, formatDate } from "../../lib/api";

export default function AdminInvitesPage() {
  const [invites, setInvites] = useState([]);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", name: "", role: "client", note: "", locale: "it" });
  const [submitting, setSubmitting] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const load = useCallback(
    () =>
      api
        .get("/admin/invites")
        .then(({ data }) => setInvites(data))
        .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail))),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/invites", form);
      setLastInvite(data);
      setForm({ email: "", name: "", role: "client", note: "", locale: form.locale });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Revocare questo invito?")) return;
    try {
      await api.delete(`/admin/invites/${id}`);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const buildLink = (token) => {
    return `${window.location.origin}/accept-invite/${token}`;
  };

  return (
    <PortalLayout>
      <section className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8" data-testid="admin-invites-page">
        <div className="br-fade-up">
          <div className="br-overline">Inviti</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
            Inviti <em>nominativi</em>.
          </h1>
          <p className="text-[1.05rem] text-[color:var(--br-text)] mt-3 max-w-[640px]">
            Link a uso unico, validità 7 giorni. Il destinatario imposta la propria password al primo accesso.
          </p>
        </div>

        <div className="mt-10 grid lg:grid-cols-2 gap-8 br-fade-up br-delay-1">
          <form onSubmit={submit} className="br-card p-7 space-y-5" data-testid="admin-invite-form">
            <h2 className="font-serif-display text-[1.6rem]">Nuovo invito</h2>
            <label className="block">
              <span className="br-overline block mb-2">Email</span>
              <input
                type="email"
                className="br-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="invite-form-email"
              />
            </label>
            <label className="block">
              <span className="br-overline block mb-2">Nome completo</span>
              <input
                className="br-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="invite-form-name"
              />
            </label>
            <label className="block">
              <span className="br-overline block mb-2">Ruolo</span>
              <select
                className="br-input"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="invite-form-role"
                style={{ background: "var(--br-bg-2)" }}
              >
                <option value="client">Cliente</option>
                <option value="admin">Amministratore</option>
              </select>
            </label>
            <label className="block">
              <span className="br-overline block mb-2">Lingua email</span>
              <select
                className="br-input"
                value={form.locale}
                onChange={(e) => setForm({ ...form, locale: e.target.value })}
                data-testid="invite-form-locale"
                style={{ background: "var(--br-bg-2)" }}
              >
                <option value="it">Italiano (IT)</option>
                <option value="en">English (EN)</option>
                <option value="no">Norsk (NO)</option>
                <option value="de">Deutsch (DE)</option>
              </select>
            </label>
            <label className="block">
              <span className="br-overline block mb-2">Note interne (opzionale)</span>
              <input
                className="br-input"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="invite-form-note"
              />
            </label>
            {error && (
              <div className="text-[0.85rem] py-3 px-4 border" style={{ borderColor: "var(--br-red)", color: "var(--br-red)", background: "rgba(179,107,107,0.06)" }} data-testid="admin-invite-error">
                {error}
              </div>
            )}
            <button type="submit" className="br-btn-primary w-full" disabled={submitting} data-testid="invite-form-submit">
              {submitting ? "Generazione…" : "Genera invito"}
            </button>
          </form>

          <div className="br-card p-7" data-testid="admin-last-invite">
            <h2 className="font-serif-display text-[1.6rem]">Ultimo invito generato</h2>
            {lastInvite ? (
              <div className="mt-4 space-y-4">
                <div>
                  <div className="br-overline">Destinatario</div>
                  <div className="font-mono-fin text-[1rem] text-[color:var(--br-parchment)] mt-2">{lastInvite.email}</div>
                </div>
                <div>
                  <div className="br-overline">Link a uso unico</div>
                  <div
                    className="font-mono-fin text-[0.75rem] mt-2 p-3 break-all"
                    style={{ background: "var(--br-bg-3)", border: "1px solid var(--br-line-bright)", color: "var(--br-bronze-soft)" }}
                    data-testid="last-invite-link"
                  >
                    {buildLink(lastInvite.token)}
                  </div>
                  <button
                    onClick={() => navigator.clipboard.writeText(buildLink(lastInvite.token))}
                    className="br-btn-outline mt-3"
                    style={{ padding: "8px 14px" }}
                    data-testid="copy-invite-link"
                  >
                    Copia link
                  </button>
                </div>
                <div className="text-[0.78rem] text-[color:var(--br-text-dim)]">
                  Scade il {formatDate(lastInvite.expires_at)}
                </div>
              </div>
            ) : (
              <p className="text-[0.92rem] text-[color:var(--br-text-dim)] mt-3">
                Compila il form a sinistra per generare un nuovo invito. Il link comparirà qui pronto da copiare.
              </p>
            )}
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif-display text-[2rem] mb-6">Inviti <em>recenti</em></h2>
          <div className="br-card overflow-x-auto" data-testid="admin-invites-table">
            <table className="w-full text-left">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                  <Th>Email</Th>
                  <Th>Nome</Th>
                  <Th>Ruolo</Th>
                  <Th>Creato</Th>
                  <Th>Scadenza</Th>
                  <Th>Stato</Th>
                  <Th align="right">Azioni</Th>
                </tr>
              </thead>
              <tbody>
                {invites.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-8 text-center text-[color:var(--br-text-dim)]">
                      Nessun invito generato.
                    </td>
                  </tr>
                ) : (
                  invites.map((inv) => {
                    const expired = !inv.used_at && new Date(inv.expires_at) < new Date();
                    const status = inv.used_at ? "Usato" : expired ? "Scaduto" : "Attivo";
                    const color = inv.used_at ? "var(--br-text-dim)" : expired ? "var(--br-red)" : "var(--br-bronze)";
                    return (
                      <tr key={inv.id} className="border-b" style={{ borderColor: "var(--br-line)" }} data-testid={`invite-row-${inv.id}`}>
                        <Td className="font-mono-fin text-[0.85rem] text-[color:var(--br-parchment)]">{inv.email}</Td>
                        <Td>{inv.name}</Td>
                        <Td className="font-mono-fin uppercase text-[0.7rem]" style={{ color: inv.role === "admin" ? "var(--br-bronze)" : "var(--br-text-dim)" }}>{inv.role}</Td>
                        <Td className="font-mono-fin text-[color:var(--br-text-dim)]">{formatDate(inv.created_at)}</Td>
                        <Td className="font-mono-fin text-[color:var(--br-text-dim)]">{formatDate(inv.expires_at)}</Td>
                        <Td>
                          <span className="font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase border px-2 py-1" style={{ borderColor: color, color }}>
                            {status}
                          </span>
                        </Td>
                        <Td align="right">
                          {!inv.used_at && (
                            <>
                              <button
                                onClick={() => navigator.clipboard.writeText(buildLink(inv.token))}
                                className="br-btn-outline mr-2"
                                style={{ padding: "6px 12px", fontSize: "0.66rem" }}
                                data-testid={`copy-link-${inv.id}`}
                              >
                                Copia
                              </button>
                              <button
                                onClick={() => remove(inv.id)}
                                className="br-btn-outline"
                                style={{ padding: "6px 12px", fontSize: "0.66rem", borderColor: "var(--br-red)", color: "var(--br-red)" }}
                                data-testid={`revoke-invite-${inv.id}`}
                              >
                                Revoca
                              </button>
                            </>
                          )}
                        </Td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </PortalLayout>
  );
}

function Th({ children, align = "left" }) {
  return <th className="px-5 py-4 font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase font-medium" style={{ color: "var(--br-text-dim)", textAlign: align }}>{children}</th>;
}
function Td({ children, align = "left", className = "", style }) {
  return <td className={`px-5 py-4 text-[0.9rem] ${className}`} style={{ textAlign: align, ...style }}>{children}</td>;
}
